import siteSetting from "../config/env";

const API_URL = siteSetting.api.v2;

function getAuthHeaders() {
  return {
    'Content-Type': 'application/json',
    'accessToken': localStorage.getItem('accessToken'),
  };
}

export const clientService = {
  getClients,
  updateClientSharing,
  getOwnershipBreakdown,
  exportClients,
  validateMerge,
  getMergePreview,
  mergeClients
};

/**
 * Get clients with access control
 */
async function getClients({ page = 1, limit = 50, search = '', filter = 'all', sortBy = 'name', sortOrder = 'asc' }) {
  const queryParams = new URLSearchParams({
    page,
    limit,
    search,
    filter,
    sortBy,
    sortOrder
  });
  
  const response = await fetch(`${API_URL}/hairdresser/clients-v2?${queryParams}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch clients');
  }
  
  return response.json();
}

/**
 * Update client sharing settings (self-employed only)
 * @param {boolean} clientShare - true to enable sharing, false to disable
 */
async function updateClientSharing(clientShare) {
  const response = await fetch(`${API_URL}/hairdresser/client-sharing-v2`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ clientShare: clientShare })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update client sharing');
  }
  
  return response.json();
}

/**
 * Get ownership breakdown
 */
async function getOwnershipBreakdown() {
  const response = await fetch(`${API_URL}/hairdresser/clients/ownership`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch ownership breakdown');
  }
  
  return response.json();
}

/**
 * Export clients
 * @param {string} format - 'csv' or 'json'
 */
async function exportClients(format = 'csv') {
  const response = await fetch(`${API_URL}/hairdresser/clients/export?format=${format}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to export clients');
  }
  
  if (format === 'csv') {
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clients_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return { success: true, message: 'Clients exported successfully' };
  }
  
  return response.json();
}

/**
 * Validate merge request
 * @param {string[]} clientIds - Array of all client IDs to merge (must be 2+)
 */
async function validateMerge(clientIds) {
  const response = await fetch(`${API_URL}salon/clients/validate-merge`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      clientIds
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to validate merge');
  }
  
  return response.json();
}

/**
 * Get merge preview
 * @param {string[]} clientIds - Array of all client IDs to merge (must be 2+)
 */
async function getMergePreview(clientIds) {
  const response = await fetch(`${API_URL}salon/clients/merge-preview`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      clientIds
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get merge preview');
  }
  
  return response.json();
}

/**
 * Merge clients
 * @param {string} primaryUserId - The primary client ID to keep
 * @param {string[]} mergeUserIds - Array of client IDs to merge into primary
 * @param {boolean} preserveNotes - Whether to preserve hairdresser notes (default: true)
 * @param {string} mergedNotes - Optional custom merged notes to use instead of auto-combining
 */
async function mergeClients(primaryUserId, mergeUserIds, preserveNotes = true, mergedNotes = null) {
  const body = {
    primaryUserId,
    mergeUserIds,
    preserveNotes
  };
  
  // If custom merged notes provided, include them
  if (mergedNotes !== null && mergedNotes !== undefined) {
    body.customNote = mergedNotes;
  }
  
  const response = await fetch(`${API_URL}/salon/clients/merge`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to merge clients');
  }
  
  return response.json();
}